import React from 'react';
import clsx from 'clsx';
import { match } from 'ts-pattern';

import { toKebabCase } from '@ovpn-ui/utils';
import { CircleFilled } from '@ovpn-ui/icons';
import { Tooltip, Text, TextProps, StatusIndicator, StatusIndicatorVariant } from '@ovpn-ui/core';

import { ConnectionStatus, StatusTypes } from '../../models';

import { Wrapper, Loader } from './StatusCell.styles';

type StatusCellProps = {
    status?: string | null;
    className?: string;
    indicatorOnly?: boolean;
    textProps?: TextProps;
};

export function StatusCell({ status, className, indicatorOnly, textProps = {} }: StatusCellProps) {
    if (status === undefined) {
        return <Loader width="80%" variant="rect" />;
    }

    const statusText = StatusTypes.friendlyString(status);
    const cn = clsx(className, 'status', `status-${toKebabCase(statusText)}`);

    const indicatorVariant = match(status as ConnectionStatus | null)
        .returnType<StatusIndicatorVariant>()
        .with(null, () => 'information')
        .with('ONLINE', () => 'success')
        .with('ONLINE_WITH_ISSUES', () => 'attention')
        .otherwise(() => 'warning');

    const statusElement = (
        <Wrapper className={cn}>
            <StatusIndicator
                variant={indicatorVariant}
                data-testid="status-icon"
                icon={<CircleFilled strokeWidth={10} />}
            />
            {!indicatorOnly && (
                <Text variant="textSm" weight="medium" className="status-label" {...textProps}>
                    {statusText}
                </Text>
            )}
        </Wrapper>
    );

    if (indicatorOnly) {
        return <Tooltip trigger={statusElement}>{statusText}</Tooltip>;
    }

    return statusElement;
}
